// M3 rig charters (M3_SPEC §2.2): charter IN a contractor's rig + crew at the day-rate market (a contractor-crewed
// rig on the M2 contract-drilling path, billed daily in P&L `charter`: mob rate while mobilizing, standby rate while
// waiting on weather, a minimum term, an early-termination fee), and charter OUT your idle offshore rigs from a board
// whose size follows fleet utilization (1981 boom → 1986 bust). Rigs can be stacked in port (10% upkeep).
import { range, int, pick } from '../../core/rng.js';
import { eqDef, fieldDef, stat, yf } from '../defs.js';
import { CHARTER, utilization, dayRate } from '../../data/m3/rules.js';
import { m3, m3On, plotWater, envelope, envelopeProblem, charterRate, distanceToShore, rigBuiltYear } from './opsutil.js';
import { platformAt, stampWell } from './offshore.js';
import { createWell, setTask } from '../drilling.js';
import { ensureMud } from '../rotary.js';
import { fieldOpen } from '../survey.js';
import { spend, earn, notify, nextId, fmt$, log } from '../util.js';
import { activeRigCrew } from '../crew.js';
import { fieldMaxWater } from '../../data/m3/fields.js';
import { m4DayRate, m4CharterOfferCount } from '../m4/marketRigs.js'; // M4-B: offshore board 2000+ (floater utilization, DAY_RATES_M4)

const ok = (msg, extra = {}) => ({ ok: true, msg, ...extra });
const no = (msg) => ({ ok: false, msg });
export const BOARD_EVERY_DAYS = 30;
export const BOARD_MAX = 6;
export const STACK_UPKEEP = 0.1;

const contractorBit = (y) => ['pdc_bit', 'journal_tricone', 'sealed_bearing_tricone', 'carbide_tricone', 'jet_tricone', 'tricone_bit'].find((id) => eqDef(id) && eqDef(id).year <= y) ?? null;

export function charterQuote(state, defId, fieldId, plot) {
  const def = eqDef(defId);
  if (!def || def.cat !== 'rig') return { ok: false, reason: 'Not a rig.' };
  const y = yf(state);
  if ((def.year ?? 9999) > Math.floor(y)) return { ok: false, reason: `No contractor runs a ${def.name} before ${def.year}.` };
  const water = plotWater(fieldId, plot);
  const bad = envelopeProblem(def, Math.floor(y), water, 0);
  const rate = charterRate(state, def, utilization(y));
  const nm = distanceToShore(fieldId) / 1.15;
  const mobDays = Math.ceil(stat(def, 'rigUpDays', 5) + CHARTER.mobDaysPer100Nm * nm / 100);
  return { ok: !bad, reason: bad, defId, water, dayRate: rate, mobDays, minDays: CHARTER.minDays, mobRate: rate * CHARTER.mobRateMult, standbyRate: rate * CHARTER.standbyRateMult, maxBml: stat(def, 'maxDepth', 0) - water };
}

export function charterRigImpl(state, defId, fieldId, plot, { minDays = CHARTER.minDays, target = null } = {}) {
  if (!m3On(state)) return no('The offshore day-rate market starts after the war (1946).');
  const f = state.fields[fieldId];
  const fdef = fieldDef(fieldId);
  const p = f?.plots[plot];
  if (!p) return no('No such plot.');
  if (!fieldOpen(state, fieldId)) return no(`${fdef.name} isn't open yet.`);
  if (fdef.serviceOnly) return no(`${fdef.name} belongs to ${fdef.serviceOnly.operator}.`);
  if (!p.owned) return no('Lease the plot first.');
  if (p.wellId) return no('There is already a well on that plot.');
  const q = charterQuote(state, defId, fieldId, plot);
  if (!q.ok) return no(q.reason);
  const def = eqDef(defId);
  if (stat(def, 'needsPlatform', false) && !platformAt(state, fieldId, plot, { needRigSlot: true })) return no(`A ${def.name} needs a platform with a free slot here.`);
  const days = Math.max(CHARTER.minDays, Math.round(minDays));
  if (state.company.cash < q.dayRate * 30) return no(`The contractor wants a month's hire (${fmt$(q.dayRate * 30)}) in the bank.`);
  const pseudo = { uid: null, defId, crewIds: [], bitUid: null };
  const tgt = Math.max(100, Math.min(target ?? q.maxBml, q.maxBml));
  const well = createWell(state, pseudo, fieldId, plot, tgt);
  well.rigId = null;
  well.rigType = 'rotary';
  well.target = tgt;
  well.contract = { perFt: 0, maxDepth: q.maxBml, startDay: state.day, readyDay: state.day + q.mobDays, billed: 0, ft: 0, rigDefId: defId, bitDefId: contractorBit(Math.floor(yf(state))),
    charter: { defId, dayRate: Math.round(q.dayRate), startDay: state.day, minDays: days, mobDays: q.mobDays, billedDays: 0, closed: false } };
  well.poreGuess = Math.round(range(state, -0.5, 0.5) * 100) / 100;
  ensureMud(state, well);
  well.status = 'RIGGING_UP';
  stampWell(state, well, q.water);
  const pf = platformAt(state, fieldId, plot, { wellId: well.id });
  if (pf) { well.platformUid = pf.uid; pf.slotsUsed.push(well.id); }
  setTask(state, well, 'CONTRACT_MOB', q.mobDays);
  m3(state).supply[well.id] = envelope(def, Math.floor(yf(state))).deckStorageDays;
  return ok(`Chartered a ${def.name} at ${fmt$(q.dayRate)}/day (${days}-day minimum), on location in ${q.mobDays} days.`, { wellId: well.id });
}

const STANDBY = new Set(['WOW', 'NO_SUPPLY', 'ICE_SEASON', 'NO_CREW']);
export function tickCharters(state) {
  for (const w of Object.values(state.wells)) {
    const c = w.contract?.charter;
    if (!c || c.closed) continue;
    const done = w.contract.doneDay != null || !['RIGGING_UP', 'DRILLING', 'PAUSED', 'TRIPPING', 'RUNNING_CASING', 'FISHING', 'STUCK', 'SHUT_IN_KICK', 'KILLING', 'CEMENTING', 'BLOWOUT', 'CLEANOUT', 'COMPLETING'].includes(w.status);
    if (done) { closeCharter(state, w, false); continue; }
    const storm = state.fields[w.fieldId]?.storm;
    const stormNow = storm && state.day >= storm.startDay && state.day <= storm.untilDay;
    const mult = w.status === 'RIGGING_UP' ? CHARTER.mobRateMult : (STANDBY.has(w.problem?.type) || stormNow || w.evacuated) ? CHARTER.standbyRateMult : 1;
    const amt = c.dayRate * mult;
    spend(state, amt, 'charter', false);
    w.cost += amt;
    c.billedDays++;
  }
}

function closeCharter(state, w, early) {
  const c = w.contract.charter;
  const left = Math.max(0, c.minDays - c.billedDays);
  const fee = (early ? Math.min(left, CHARTER.earlyTerminationDays) : left) * c.dayRate;
  if (fee > 0) { spend(state, fee, 'charter', false); w.cost += fee; }
  c.closed = true;
  c.closedDay = state.day;
  w.contract.doneDay ??= state.day;
  log(state, `${eqDef(c.defId)?.name} released from ${w.name} after ${c.billedDays} days${fee > 0 ? ` (${fmt$(fee)} ${early ? 'early-termination fee' : 'minimum-term balance'})` : ''}.`);
}

export function releaseCharterImpl(state, wellId) {
  const w = state.wells[wellId];
  if (!w?.contract?.charter || w.contract.charter.closed) return no('No chartered rig on that well.');
  for (const t of Object.values(state.timers)) if (t.refId === w.id && t.kind !== 'RESEARCH') delete state.timers[t.id];
  w.timerId = null;
  if (!['AT_TD', 'DRY', 'PRODUCING', 'SHUT_IN'].includes(w.status)) w.status = 'SUSPENDED';
  closeCharter(state, w, true);
  return ok(`Charter released from ${w.name}.`);
}

// ---- charter out ------------------------------------------------------------------------------------------------
export const charterOfferCount = (y) => Math.round(BOARD_MAX * Math.max(0, Math.min(1, (utilization(y) - 0.4) / 0.58)));

function offshoreFieldIds(state) {
  return Object.keys(state.fields).filter((fid) => { const d = fieldDef(fid); return d && fieldMaxWater(d) > 20 && fieldOpen(state, fid); });
}

function classFor(water) { return water <= 20 ? 'barge' : water <= 350 ? 'jackup' : water <= 1500 ? 'semisub' : 'drillship'; }

export function refreshCharterBoard(state) {
  const M = m3(state);
  const y = yf(state);
  const fids = offshoreFieldIds(state);
  const n = fids.length ? (m4CharterOfferCount(y) ?? charterOfferCount(y)) : 0; // M4-B
  const board = [];
  for (let i = 0; i < n; i++) {
    const fid = pick(state, fids);
    const fd = fieldDef(fid);
    const plot = int(state, 0, (fd.grid?.w ?? 6) * (fd.grid?.h ?? 4) - 1);
    const water = plotWater(fid, plot);
    const cls = classFor(water);
    const days = int(state, 30, 365);
    const dr = (k) => m4DayRate(k, y) ?? dayRate(k, y); // M4-B
    const rate = Math.round(Math.max(dr(cls), dr('jackup') * 0.5) * (0.85 + 0.3 * range(state, 0, 1)) / 100) * 100;
    board.push({ id: nextId(state, 'co'), fieldId: fid, plot, waterDepthFt: water, rigClass: cls, days, rate, operator: fd.serviceOnly?.operator ?? null, postedDay: state.day, expiresDay: state.day + BOARD_EVERY_DAYS });
  }
  M.charterBoard = board;
  M.boardDay = state.day;
  return board;
}

export function acceptCharterOutImpl(state, offerId, rigUid) {
  const M = m3(state);
  const o = M.charterBoard.find((x) => x.id === offerId);
  if (!o) return no('That charter has been taken.');
  const rig = state.rigs[rigUid];
  if (!rig) return no('No such rig.');
  if (rig.wellId || rig.charterOut) return no(`${rig.name} is busy.`);
  const def = eqDef(rig.defId);
  const bad = envelopeProblem(def, rigBuiltYear(rig), o.waterDepthFt, 0);
  if (bad) return no(bad);
  if (!activeRigCrew(state, rig).some((c) => c.role === 'driller')) return no(`${rig.name} has no driller.`);
  rig.charterOut = { offerId, fieldId: o.fieldId, rate: o.rate, startDay: state.day, untilDay: state.day + o.days, operator: o.operator, earned: 0 };
  rig.stacked = false;
  M.charterBoard = M.charterBoard.filter((x) => x.id !== offerId);
  return ok(`${rig.name} chartered to ${o.operator ?? 'an operator'} at ${fieldDef(o.fieldId)?.name}: ${fmt$(o.rate)}/day for ${o.days} days.`);
}

export function stackRigImpl(state, rigUid, on = true) {
  const rig = state.rigs[rigUid];
  if (!rig) return no('No such rig.');
  if (on && (rig.wellId || rig.charterOut)) return no(`${rig.name} is working.`);
  rig.stacked = !!on;
  return ok(on ? `${rig.name} stacked in port (upkeep ${Math.round(STACK_UPKEEP * 100)}% of the day rate).` : `${rig.name} reactivated.`);
}

export function tickCharterOut(state) {
  const M = m3(state);
  if (state.day - (M.boardDay ?? 0) >= BOARD_EVERY_DAYS && Object.values(state.rigs).some((r) => eqDef(r.defId)?.stats?.offshore)) refreshCharterBoard(state);
  for (const rig of Object.values(state.rigs)) {
    if (rig.stacked) { spend(state, charterRate(state, eqDef(rig.defId), 0.7) * STACK_UPKEEP, 'other', false); continue; }
    const co = rig.charterOut;
    if (!co) continue;
    const st = state.fields[co.fieldId]?.storm;
    const fm = CHARTER.forceMajeureStorm && st && state.day >= st.startDay && state.day <= st.untilDay;
    if (!fm) { earn(state, co.rate, 'revenue'); co.earned += co.rate; }
    rig.condition = Math.max(0, rig.condition - 0.02);
    if (state.day >= co.untilDay) {
      rig.charterOut = null;
      rig.location = { fieldId: co.fieldId, plot: 0 };
      rig.at = { fieldId: co.fieldId, plot: 0 };
      notify(state, 'contractDone', { rigUid: rig.uid, text: `${rig.name} is back from charter: earned ${fmt$(co.earned)}.` });
    }
  }
}

export function charterBoardView(state) {
  const M = state.m3;
  return (M?.charterBoard ?? []).map((o) => ({ ...o, fieldName: fieldDef(o.fieldId)?.name, value: o.rate * o.days,
    fits: Object.values(state.rigs).filter((r) => !r.wellId && !r.charterOut && !envelopeProblem(eqDef(r.defId), rigBuiltYear(r), o.waterDepthFt, 0)).map((r) => r.uid) }));
}

